import React, { useState } from "react";
import { useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUser } from "../../../../store/authSlice";
import { useGetNcsModemRecoveryQuery } from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  fieldClass,
  btnGhost,
  Badge,
  MODEM_STATE_LABEL,
  MODEM_STATE_TONE,
  fmtDate,
} from "./config";
import { downloadWorkbook } from "../../../../services/netplusCustomerStatus";

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:5001";
const resolveUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${API_ORIGIN}${u}`;

const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

const authorChip = (by, byName, meId) =>
  by && by._id && meId && String(by._id) === String(meId)
    ? "You"
    : initialsOf((by && by.name) || byName);

const activityOf = (c) => {
  const remarks = (c?.remarks || []).map((r) => ({
    at: r.at,
    by: r.by,
    byName: r.byName,
    text: r.text,
    kind: "remark",
  }));
  const visits = (c?.visits || []).map((v) => ({
    at: v.at,
    by: v.by,
    byName: v.byName,
    text: v.note,
    photoUrl: v.photoUrl,
    kind: "visit",
  }));
  return [...remarks, ...visits].sort(
    (a, b) => new Date(b.at || 0) - new Date(a.at || 0),
  );
};

const NcsModemHandover = () => {
  const meId = useSelector(selectUser)?.localId;
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [historyCase, setHistoryCase] = useState(null);

  const { data, isFetching } = useGetNcsModemRecoveryQuery({
    partnerCode,
    state: "handed-over",
    q: qLive,
    from: fromDate,
    to: toDate,
    page,
    limit: 50,
  });
  const rows = data?.cases || [];
  const totalPages = data?.totalPages || 1;

  return (
    <>
      <PageTitle activeMenu="Modem Hand Over" motherMenu="Netplus" />
      <section className={panelClass}>
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Modems submitted to the company
          </h2>
          <form
            className="ml-auto flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQLive(q.trim());
              setPage(1);
            }}
          >
            <PartnerPicker value={partnerCode} onChange={setPartnerCode} />
            <input
              type="date"
              className={fieldClass}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              title="Handed over from"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              className={fieldClass}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              title="Handed over to"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
            <input
              className={`${fieldClass} w-full min-w-[10rem] sm:w-56`}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              placeholder="Name / phone / id / MAC"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className={btnGhost} type="submit">
              Search
            </button>
            <button
              type="button"
              className={btnGhost}
              onClick={() =>
                downloadWorkbook(partnerCode, { view: "modem-handover" })
              }
            >
              Export
            </button>
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">MAC</th>
                <th className="px-4 py-3 text-center">Recovered</th>
                <th className="px-4 py-3 text-center">Handed over</th>
                <th className="px-4 py-3 text-left">By</th>
                <th className="px-4 py-3 text-left">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    Nothing handed over yet.
                  </td>
                </tr>
              ) : (
                rows.map((c) => {
                  const hist = activityOf(c);
                  const last = hist[0];
                  return (
                    <tr key={c._id} className="align-top hover:bg-emerald-50/30">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-800">
                          {c.name || c.userId}
                        </div>
                        <div
                          className="flex flex-wrap gap-x-1 font-bold"
                          style={{ color: "#222222", fontSize: 14 }}
                        >
                          <span className="whitespace-nowrap">m:{c.phone || "—"},</span>
                          <span className="whitespace-nowrap">id:{c.userId}</span>
                        </div>
                        {last && (
                          <button
                            type="button"
                            onClick={() => setHistoryCase(c)}
                            className="mt-1 flex w-full max-w-[340px] items-center gap-1.5 text-left text-[13px] transition hover:opacity-75"
                          >
                            <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                              {authorChip(last.by, last.byName, meId)}
                            </span>
                            <span
                              className="min-w-0 flex-1 truncate font-medium"
                              style={{ color: "#1d4ed8" }}
                            >
                              {last.text || "(no remark)"}
                            </span>
                            <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                              {hist.length}
                            </span>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {c.macId || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge tone={MODEM_STATE_TONE[c.state]}>
                          {MODEM_STATE_LABEL[c.state] || c.state}
                        </Badge>
                        <div className="text-[11px] text-gray-400">
                          {fmtDate(c.closedOn)}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-600">
                        {fmtDate(c.handedOverAt)}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-700">
                        {c.handedOverBy?.name || c.handedOverByName || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {c.handoverNote || "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{data?.totalCount ?? 0} total</span>
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {historyCase && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setHistoryCase(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {historyCase.name || historyCase.userId}
              </h3>
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setHistoryCase(null)}
              >
                Close
              </button>
            </div>
            <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {activityOf(historyCase).map((h, i) => (
                <div
                  key={i}
                  className={`px-4 py-2 text-sm ${i % 2 === 1 ? "bg-gray-50" : "bg-white"}`}
                  style={{ color: h.kind === "visit" ? "#3730a3" : "#1d4ed8" }}
                >
                  <span className="font-semibold">{fmtDate(h.at)}</span>{" "}
                  <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                    {authorChip(h.by, h.byName, meId)}
                  </span>{" "}
                  <span className="text-xs opacity-70">[{h.kind}]</span>{" "}
                  <span className="font-medium">— {h.text || "(no remark)"}</span>
                  {h.photoUrl && (
                    <a
                      href={resolveUrl(h.photoUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block"
                    >
                      <img
                        src={resolveUrl(h.photoUrl)}
                        alt="visit"
                        className="max-h-40 rounded-lg border border-gray-200"
                      />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NcsModemHandover;
